// src/prototype/pages/EAOpportunitiesPage.tsx
// ============================================================
// EA STEP 2: EVALUATE & SHORTLIST
// "Which opportunities are credible and relevant enough to carry forward?"
//
// Two tabs (EATabBar): Portfolio Strategy ('report') and Use Case Explorer
// ('explore'). The explorer lists ranked candidates as compact verdict-led rows
// (ExplorerRow); the whole row opens that use case's Decision Brief, which is where
// the analytical depth now lives. Rows for use cases already proceeded to Execute
// are tinted.
//
// UPDATED: Uses useDemoPlan hook for Pro/Freemium gating
// UPDATED: "Adjust Weights →" moved to banner (removed separate band)
// UPDATED: Now imports from shared mockOpportunities.ts for consistency
//          with EAValidationPage - banner values will match!
// UPDATED (drilldown redesign): expandable rows — with their inline scenario tabs,
//          assumption flagging and Evaluation Tools — were replaced by ExplorerRow.
//          Those surfaces live on the Decision Brief and ROI Model instead.
//
// KEY: Uses EAContext for finalistIds and categoryWeights so selections and weights
// persist to Step 3.
// ============================================================

import { baseAnnualValueMillions, baseRoiEstimate, formatMillionsRange, sumBaseAnnualValueMillions } from '../roi/hubFigures';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DEMO_CLIENT } from '../config/demoClient';
import useDemoUploads from '../hooks/useDemoUploads';
import { commitmentsByOpportunity } from '../utils/initiativesImport';
import { showDemoControls } from '../config/demoFlags';
import { 
  ArrowLeft, 
  ArrowRight, 
  MessageCircle, 
  X,
  Zap,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { EAJourneyBarFrame } from '../components/EAJourneyBarFrame';
import { OIRReportView } from '../components/oir/report/OIRReportView';
import { type SortOption } from '../components/oir/OIRSortBar';
import { EATabBar, type EvaluateTab } from '../components/EATabBar';
import { useEA } from '../contexts/EAContext';
import { useEntitlements } from '../hooks/useEntitlements';
import { EALockedOpportunityRow } from '../components/EALockedOpportunityRow';
import { ExplorerRow } from '../components/ExplorerRow';
import { AsOfChip } from '../components/hub/AsOfChip';
import { CompareTray } from '../components/CompareTray';
import { CompareTable } from '../components/CompareTable';
import { getDecisionBrief } from '../data/decisionBrief';
import { useCompareSelection, MIN_COMPARE } from '../hooks/useCompareSelection';
import { EXPLORER_COLS } from '../components/explorerColumns';
import { readFocusParam, readTabParam } from '../utils/exportReturn';
import { useExecutePortfolio } from '../hooks/useExecutePortfolio';
import { calculateWeightedScore } from '../utils/weighting';
import { useOptionalHelp } from '../../help/useHelp';

// CRITICAL: Import shared mock data for consistency with EAValidationPage
import { 
  MOCK_OPPORTUNITIES as SHARED_OPPORTUNITIES,
  MOCK_TOTAL_COUNT,
  type Opportunity as SharedOpportunity,
} from '../data/mockOpportunities';
// Composite (regenerated from DIM6 via acme-demo-v1) is the SINGLE rank basis. Featured opps rank on
// their composite; fillers rank below the featured band. Effort/TTV are display tags, not rank inputs.
import { OPP_TO_UC, compositeForUseCase } from '../data/adapters/stage300FeaturedContent';

import useEvaluationRun from '../hooks/useEvaluationRun';
import { EAEvaluationRunOverlay } from '../components/EAEvaluationRunOverlay';
import { computeIncludedCount } from '../utils/opportunityRanking';
import { PriorityMatrix } from '../components/evaluate/PriorityMatrix';
import { buildMatrixPoints } from '../data/priorityMatrix';
// Mock personalization data

// Client name — single source of truth (src/prototype/config/demoClient.ts)
const MOCK_CLIENT_NAME = DEMO_CLIENT.name;
/** Short label for the one-line filter bar (\"ACME Health System\" -> \"ACME Health\"). */
const MOCK_CLIENT_SHORT = MOCK_CLIENT_NAME.replace(/\s+(Health\s+)?System$/i, (m) => (/health/i.test(m) ? ' Health' : ''));

// ============================================================
// ROI FORMATTING UTILITIES
// Handles both legacy mock data (values in millions like 7.9)
// and real BigQuery data (values in raw dollars like 7887250)
// ============================================================



const toMillions = (value: number): number => {
  if (value < 100) return value;
  return value / 1_000_000;
};

// ============================================================
// TYPES (Page-specific extensions)
// ============================================================

interface SurfacedReason {
  contextLabel: string;
  contextValue: string;
  relevance: string;
}

type ConfidenceLabel = 'High' | 'Medium' | 'Low';

type Opportunity = Omit<SharedOpportunity, 'surfacedReasons' | 'assumptions' | 'scenarioAssumptions'> & {
  surfacedReasons: SurfacedReason[];
  assumptions: (SharedOpportunity['assumptions'][0] & { id: string })[];
  scenarioAssumptions: {
    base: (SharedOpportunity['assumptions'][0] & { id: string })[];
    conservative: (SharedOpportunity['assumptions'][0] & { id: string })[];
    aggressive: (SharedOpportunity['assumptions'][0] & { id: string })[];
  };
  confidence: ConfidenceLabel;
};

// E3 (Oct 21, revised): one-line filter bar. "{Client}: Projects" narrows to the client's own
// use cases; "Committed" toggles between excluding committed use cases (default: the client has
// already committed to them, so they do not need evaluating) and showing only committed ones.
// Revised again (Oct 21): each is a three-way control, Include / Exclude / Only. Defaults:
// Projects Include, Committed Exclude. Projects Only + Committed Only = the union (anything
// ACME-related), not the intersection.
type AcmeMode = 'include' | 'exclude' | 'only';
const ACME_MODES: readonly AcmeMode[] = ['include', 'exclude', 'only'];

/** Committed default differs by tab: the Explorer hides committed use cases (they do not need
 *  evaluating); the Priority Matrix shows the whole portfolio, committed included. */
export function comDefaultFor(tab: string | null | undefined): AcmeMode {
  return tab === 'matrix' ? 'include' : 'exclude';
}

/** Pure ACME-filter predicate (exported for tests). */
/** Portfolio filter (Oct 21): Include / Exclude / Only the use cases in the user's portfolio. */
export function passesPortfolioFilter(inPortfolio: boolean, mode: AcmeMode): boolean {
  return mode === 'include' || (mode === 'only' ? inPortfolio : !inPortfolio);
}

export function passesAcmeFilters(isProject: boolean, isCommittedUc: boolean, proj: AcmeMode, com: AcmeMode): boolean {
  if (proj === 'only' && com === 'only') return isProject || isCommittedUc;
  if (proj === 'only' && !isProject) return false;
  if (proj === 'exclude' && isProject) return false;
  if (com === 'only' && !isCommittedUc) return false;
  if (com === 'exclude' && isCommittedUc) return false;
  return true;
}

/** Filter state lives in the URL so views are shareable and survive prev/next. Unknown
 *  values fall back to the default; entitlements are applied after reading (see
 *  filteredOpportunities: freemium is never filtered, whatever the URL says). */
function readFilterParams(search: string) {
  const p = new URLSearchParams(search);
  const pick = <T extends string>(key: string, allowed: readonly T[], dflt: T): T => {
    const v = p.get(key);
    return v && (allowed as readonly string[]).includes(v) ? (v as T) : dflt;
  };
  return {
    // Legacy links: ?source=client meant Projects only; ?status=committed meant Committed only.
    proj: pick<AcmeMode>('proj', ACME_MODES, p.get('source') === 'client' ? 'only' : 'include'),
    com: pick<AcmeMode>('com', ACME_MODES, p.get('status') === 'committed' ? 'only' : comDefaultFor(p.get('tab'))),
    comExplicit: p.has('com') || p.get('status') === 'committed',
    port: pick<AcmeMode>('port', ACME_MODES, 'include'),
    category: p.get('cat') || 'all',
  };
}

// ============================================================
// TRANSFORM SHARED DATA - Add page-specific fields
// ============================================================

const SURFACED_REASONS_TEMPLATES: Record<string, SurfacedReason[]> = {
  'Operational Efficiency': [
    { contextLabel: 'Strategic Priority', contextValue: 'Cost reduction focus', relevance: 'Directly addresses cost reduction through improved resource utilization' },
    { contextLabel: 'Data Maturity', contextValue: 'High - operational data available', relevance: 'Your data infrastructure can support this initiative' },
  ],
  'Patient Flow': [
    { contextLabel: 'Strategic Goal', contextValue: 'Improve throughput', relevance: 'Directly aligned with your patient flow improvement goals' },
    { contextLabel: 'Data Availability', contextValue: 'Real-time feeds', relevance: 'Real-time data feeds already established' },
  ],
  'Supply Chain': [
    { contextLabel: 'Cost Pressure', contextValue: 'Supply costs up 12% YoY', relevance: 'Supply optimization is a proven cost reduction lever' },
    { contextLabel: 'Industry Benchmark', contextValue: 'Top quartile opportunity', relevance: 'Similar systems achieve 15-20% inventory reduction' },
  ],
  'Revenue Integrity': [
    { contextLabel: 'Revenue Opportunity', contextValue: 'Below benchmark performance', relevance: 'Clear opportunity to capture appropriate reimbursement' },
    { contextLabel: 'Peer Success', contextValue: 'Proven at similar institutions', relevance: 'Similar institutions have achieved significant improvement' },
  ],
  'Workforce': [
    { contextLabel: 'Labor Cost', contextValue: '52% of operating budget', relevance: 'Your team has capacity to support workforce analytics' },
    { contextLabel: 'Overtime Spend', contextValue: 'Above benchmark', relevance: 'Significant opportunity to reduce premium labor' },
  ],
  'Clinical Operations': [
    { contextLabel: 'Quality Priority', contextValue: 'Board-level metric', relevance: 'Aligns with stated quality improvement goals' },
    { contextLabel: 'Clinical Impact', contextValue: 'High patient benefit', relevance: 'Direct impact on patient outcomes' },
  ],
  'Consumer Experience': [
    { contextLabel: 'Digital Strategy', contextValue: 'Consumer-first priority', relevance: 'Aligns with your digital and consumer strategy' },
    { contextLabel: 'Engagement Gap', contextValue: 'Below peer benchmark', relevance: 'Clear opportunity for improvement' },
  ],
  'Quality & Safety': [
    { contextLabel: 'Safety Priority', contextValue: 'Zero harm initiative', relevance: 'Directly supports patient safety goals' },
    { contextLabel: 'Regulatory', contextValue: 'CMS focus area', relevance: 'Helps meet regulatory requirements' },
  ],
  'Research Analytics': [
    { contextLabel: 'Research Mission', contextValue: 'AMC priority', relevance: 'Supports academic medical center research mission' },
    { contextLabel: 'Grant Opportunity', contextValue: 'Funding available', relevance: 'May qualify for research funding' },
  ],
  'Population Health': [
    { contextLabel: 'Value-Based Care', contextValue: 'Risk contracts growing', relevance: 'Supports population health management strategy' },
    { contextLabel: 'Community Impact', contextValue: 'SDOH focus', relevance: 'Addresses social determinants of health' },
  ],
};

const MOCK_OPPORTUNITIES: Opportunity[] = SHARED_OPPORTUNITIES.map((opp) => {
  const baseConfidence = opp.scenarios?.base?.confidence ?? 0.6;
  const confidence: ConfidenceLabel = baseConfidence >= 0.7 ? 'High' : baseConfidence >= 0.4 ? 'Medium' : 'Low';

  const assumptions = (opp.assumptions || []).map((a, idx) => ({
    ...a,
    id: `${opp.id}-a-${idx}`,
  }));

  const scenarioAssumptions = {
    base: (opp.scenarioAssumptions?.base || []).map((a, idx) => ({ ...a, id: `${opp.id}-base-${idx}` })),
    conservative: (opp.scenarioAssumptions?.conservative || []).map((a, idx) => ({ ...a, id: `${opp.id}-cons-${idx}` })),
    aggressive: (opp.scenarioAssumptions?.aggressive || []).map((a, idx) => ({ ...a, id: `${opp.id}-agg-${idx}` })),
  };

  return {
    ...(opp as Omit<SharedOpportunity, 'surfacedReasons' | 'assumptions' | 'scenarioAssumptions'>),
    assumptions,
    scenarioAssumptions,
    confidence,
    surfacedReasons: SURFACED_REASONS_TEMPLATES[opp.category] || [
      { contextLabel: 'Strategic Priority', contextValue: 'Aligned with goals', relevance: `Directly supports your ${opp.category.toLowerCase()} objectives` },
      { contextLabel: 'Industry Benchmark', contextValue: 'Top quartile opportunity', relevance: 'Proven approach at peer institutions' },
    ],
  };
});

const CLIENT_USE_CASE_IDS = new Set(['opp-1', 'opp-2', 'opp-4', 'opp-6', 'opp-8', 'opp-15', 'opp-18', 'opp-23', 'opp-27', 'opp-31', 'opp-35', 'opp-42', 'opp-48', 'opp-55', 'opp-62', 'opp-68', 'opp-71', 'opp-74', 'opp-75']);


// ============================================================
// UTILITY FUNCTIONS
// ============================================================


// ============================================================
// SUB-COMPONENTS
// ============================================================







// ============================================================
// MAIN PAGE COMPONENT
// ============================================================

const ITEMS_PER_PAGE = 15;


export default function EAOpportunitiesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  
  interface RestoreState {
    scrollY: number;
    useCaseId: string;
    expanded?: boolean;
  }
  
  const { isPro, isFreemium, freemiumExplorerLimit, canViewOpportunityDetail } = useEntitlements();
  // Evaluation-run interstitial: fires once per personalization change (see useEvaluationRun).
  const evalRun = useEvaluationRun();
  // Shared live personalization state — same source the journey bar widget reads.
  const compare = useCompareSelection();
  const { portfolio, proceed } = useExecutePortfolio();
  
  const { 
    finalistIds, 
    toggleFinalist,
    categoryWeights,
    areCategoryWeightsCustomized,
  } = useEA();
  
  const [chatOpen, setChatOpen] = useState(false);
  // The Ask DVA overlay and the chat FAB share the bottom-right corner; the FAB yields.
  const helpOpen = useOptionalHelp()?.isOpen ?? false;
  const [sortOption, setSortOption] = useState<SortOption>('rank');
  // TAB IS ADDRESSABLE BY ROUTE. `?tab=` wins, then router state (in-app
  // drilldown returns), then the default. The URL form is what survives a new
  // tab — router state does not — which is why an export returning from
  // /use-case/print always landed on Portfolio Strategy before.
  const [activeTab, setActiveTab] = useState<EvaluateTab>(
    () =>
      (readTabParam(location.search) as EvaluateTab | null) ??
      (location.state?.returnToTab as EvaluateTab) ??
      'report',
  );

  // The use case the view was asked to land on, from ?focus=.
  const focusUseCaseId = readFocusParam(location.search);

  const [showFilters, _setShowFilters] = useState(false); // unused — filters now always visible
  const initialFilters = useMemo(() => readFilterParams(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [categoryFilter, setCategoryFilter] = useState<string>(initialFilters.category);
  const [projMode, setProjMode] = useState<AcmeMode>(initialFilters.proj);
  const [portMode, setPortMode] = useState<AcmeMode>(initialFilters.port);
  const [comMode, setComModeRaw] = useState<AcmeMode>(initialFilters.comExplicit ? initialFilters.com : comDefaultFor(activeTab));
  // Once the user picks a Committed mode it sticks across tabs; until then each tab uses its default.
  const [comTouched, setComTouched] = useState<boolean>(initialFilters.comExplicit);
  const setComMode = (m: AcmeMode) => { setComTouched(true); setComModeRaw(m); };

  // Committed flags come from the existing-initiatives upload (E2): a Matched + Committed row
  // marks its library use case. Nothing uploaded = nothing committed.
  const { initiatives: uploadedInitiatives } = useDemoUploads();
  const commitments = useMemo(() => commitmentsByOpportunity(uploadedInitiatives), [uploadedInitiatives]);
  const isCommitted = useCallback((id: string) => commitments.get(id)?.committed === true, [commitments]);

  // Mirror filter state into the URL (replace, not push: a filter change is not a new page).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const setOrDrop = (key: string, value: string, dflt: string) => {
      if (value === dflt) params.delete(key); else params.set(key, value);
    };
    params.delete('source'); // legacy keys, replaced by proj/com
    params.delete('status');
    setOrDrop('proj', projMode, 'include');
    setOrDrop('com', comMode, comDefaultFor(activeTab));
    setOrDrop('cat', categoryFilter, 'all');
    setOrDrop('port', portMode, 'include');
    params.delete('effort'); // Effort filter retired (Oct 21)
    params.delete('time'); // retired filters (Oct 21): never left silently applied
    params.delete('value');
    const next = params.toString();
    if (next !== location.search.replace(/^\?/, '')) {
      navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true, state: location.state });
    }
  }, [projMode, comMode, portMode, categoryFilter, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const restore = location.state?.restore as RestoreState | undefined;
    const restoreScrollY = location.state?.restoreScrollY as number | undefined;
    
    if (restore) {
      if (restore.scrollY !== undefined) {
        requestAnimationFrame(() => {
          window.scrollTo({ top: restore.scrollY, behavior: 'auto' });
        });
      }
      window.history.replaceState({}, document.title);
    }
    else if (restoreScrollY !== undefined) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: restoreScrollY, behavior: 'auto' });
      });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const uniqueCategories = useMemo(() => {
    const cats = new Set(MOCK_OPPORTUNITIES.map(o => o.category));
    return ['all', ...Array.from(cats).sort()];
  }, []);

  const finalistCount = finalistIds.size;

  // Opportunities clearing the real inclusion floor (score >= INCLUSION_THRESHOLD). Derived,
  // never hardcoded — a data change that moves the count moves the copy with it.
  const includedOpportunityCount = useMemo(
    () => computeIncludedCount(categoryWeights),
    [categoryWeights],
  );

  const rankedOpportunities = useMemo(() => {
    const withScores = MOCK_OPPORTUNITIES.map(opp => {
      // Single rank basis = the regenerated composite. Featured opps read it from the seed; fillers
      // fall back to their (effort/TTV) proxy but are capped below the featured band so they never
      // outrank a featured use case. The proxy no longer drives featured ordering.
      const composite = compositeForUseCase(OPP_TO_UC[opp.id]);
      const proxy = calculateWeightedScore(
        {
          roiEstimate: opp.roiEstimate,
          implementationEffort: opp.implementationEffort,
          timeToValue: opp.timeToValue,
        },
        categoryWeights
      );
      const base = composite ?? Math.min(58, proxy);
      return {
        ...opp,
        weightedScore: base,
      };
    });
    
    withScores.sort((a, b) => b.weightedScore - a.weightedScore);

    const roiMids = withScores.map(o => {
      const lo = toMillions(o.roiEstimate.low);
      const hi = toMillions(o.roiEstimate.high);
      return (lo + hi) / 2;
    });
    const minRoi = Math.min(...roiMids);
    const maxRoi = Math.max(...roiMids);
    const roiSpan = maxRoi - minRoi || 1;

    const effortBase: Record<string, number> = { Low: 75, Medium: 50, High: 25 };

    return withScores.map((opp, idx) => {
      const roiMid = roiMids[idx];
      const bv_axis = Math.round(20 + ((roiMid - minRoi) / roiSpan) * 70);

      const baseIE = effortBase[opp.implementationEffort] ?? 50;
      const firstMonth = parseInt(opp.timeToValue.split('-')[0]) || 12;
      const timeAdj = firstMonth < 6 ? 8 : firstMonth <= 12 ? 0 : -8;
      const payback = opp.scenarios?.base?.paybackMonths ?? 12;
      const paybackAdj = Math.max(-10, Math.min(10, Math.round(((12 - payback) / 15) * 12)));
      const ie_axis = Math.max(20, Math.min(90, Math.round(baseIE + timeAdj + paybackAdj)));

      return { ...opp, rank: idx + 1, bv_axis, ie_axis };
    });
  }, [categoryWeights]);

  // One predicate per filter group, so the option counts can apply every OTHER group (faceted
  // counts) and the list applies all of them.
  type Ranked = (typeof rankedOpportunities)[number];
  const passes = useCallback((opp: Ranked, skipAcme?: boolean): boolean => {
    if (categoryFilter !== 'all' && opp.category !== categoryFilter) return false;
    if (skipAcme) return true;
    if (!passesPortfolioFilter(finalistIds.has(opp.id), portMode)) return false;
    return passesAcmeFilters(CLIENT_USE_CASE_IDS.has(opp.id), isCommitted(opp.id), projMode, comMode);
  }, [categoryFilter, projMode, comMode, portMode, finalistIds, isCommitted]);

  const filteredOpportunities = useMemo(() => {
    // Freemium sees the FULL ranked list (rows beyond the top-N render locked);
    // filters are a Pro capability, so freemium is unfiltered whatever the URL carries.
    if (!isPro) return rankedOpportunities;
    // Ranks are NOT re-sequenced: each row keeps its portfolio rank (decision A, Oct 21).
    return rankedOpportunities.filter((opp) => passes(opp));
  }, [isPro, rankedOpportunities, passes]);

  // Counts beside the ACME controls: how many projects / committed use cases exist under the
  // current Category filter.
  const acmeCounts = useMemo(() => {
    const base = rankedOpportunities.filter((o) => passes(o, true));
    return {
      projects: base.filter((o) => CLIENT_USE_CASE_IDS.has(o.id)).length,
      committed: base.filter((o) => isCommitted(o.id)).length,
      portfolio: base.filter((o) => finalistIds.has(o.id)).length,
    };
  }, [rankedOpportunities, passes, isCommitted, finalistIds]);

  const matrixPoints = useMemo(() => buildMatrixPoints(filteredOpportunities), [filteredOpportunities]);

  const sortedOpportunities = useMemo(() => {
    if (sortOption === 'rank') return filteredOpportunities;
    return [...filteredOpportunities].sort((a, b) => {
      switch (sortOption) {
        case 'roi_high': {
          // Engine Base Case high (annual value, not ROI%).
          return baseAnnualValueMillions(b.id).high - baseAnnualValueMillions(a.id).high;
        }
        case 'payback':
        case 'time_to_value': {
          const aMonths = parseInt(a.timeToValue.split('-')[0]) || 999;
          const bMonths = parseInt(b.timeToValue.split('-')[0]) || 999;
          return aMonths - bMonths;
        }
        case 'effort': {
          const effortOrder = { Low: 0, Medium: 1, High: 2 };
          return (effortOrder[a.implementationEffort as keyof typeof effortOrder] ?? 1)
               - (effortOrder[b.implementationEffort as keyof typeof effortOrder] ?? 1);
        }
        case 'confidence': {
          const confOrder = { High: 0, Medium: 1, Low: 2 };
          const aConf = typeof a.confidence === 'number'
            ? (a.confidence >= 0.7 ? 'High' : a.confidence >= 0.4 ? 'Medium' : 'Low')
            : (a.confidence as string) || 'Medium';
          const bConf = typeof b.confidence === 'number'
            ? (b.confidence >= 0.7 ? 'High' : b.confidence >= 0.4 ? 'Medium' : 'Low')
            : (b.confidence as string) || 'Medium';
          return (confOrder[aConf as keyof typeof confOrder] ?? 1)
               - (confOrder[bConf as keyof typeof confOrder] ?? 1);
        }
        default:
          return 0;
      }
    });
  }, [filteredOpportunities, sortOption]);

  const totalPages = Math.ceil(sortedOpportunities.length / ITEMS_PER_PAGE);
  const paginatedOpportunities = useMemo(() => {
    if (!isPro) return sortedOpportunities;
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedOpportunities.slice(start, start + ITEMS_PER_PAGE);
  }, [isPro, sortedOpportunities, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, projMode, comMode, sortOption]);

  // (Removed the freemium clear-all-finalists effect: freemium may now build a top-3 portfolio
  // via the per-row Add-to-Portfolio button, so the portfolio must persist across renders.)

  const activeFilterCount = [
    categoryFilter !== 'all',
    projMode !== 'include',
    comMode !== comDefaultFor(activeTab),
    portMode !== 'include',
  ].filter(Boolean).length;

  
  
  const selectedOpportunities = rankedOpportunities.filter(o => finalistIds.has(o.id));
  const top10 = rankedOpportunities.slice(0, 10);

  // Every dollar figure on Evaluate is the engine Base Case. The report sections all read
  // `opp.roiEstimate`, so the report gets opportunities whose roiEstimate IS the Base Case
  // (raw dollars; the sections' toMillions handles the unit). Ranking above is untouched.
  const reportRanked = useMemo(
    () => rankedOpportunities.map((o) => ({ ...o, roiEstimate: { ...o.roiEstimate, ...baseRoiEstimate(o.id) } })),
    [rankedOpportunities],
  );
  const reportSelected = reportRanked.filter(o => finalistIds.has(o.id));
  const reportTop10 = reportRanked.slice(0, 10);

  // One tally for the Explorer header and the report.
  const { low: selectedRoiLow, high: selectedRoiHigh } = sumBaseAnnualValueMillions(selectedOpportunities.map(o => o.id));
  const { low: top10RoiLow, high: top10RoiHigh } = sumBaseAnnualValueMillions(top10.map(o => o.id));
  const portfolioRangeLabel = formatMillionsRange(top10RoiLow, top10RoiHigh);
  const shortlistedRangeLabel = finalistCount > 0
    ? formatMillionsRange(selectedRoiLow, selectedRoiHigh)
    : '—';


  // Handlers




  const handleClearFilters = () => {
    setCategoryFilter('all');
    setProjMode('include');
    setPortMode('include');
    setComModeRaw(comDefaultFor(activeTab));
    setComTouched(false);
  };

  // One-line filter bar (Pro only), shared by Use Case Explorer and Priority Matrix.
  const filterBarEl = isPro ? (
                <div className="border-b border-[#e5e7eb] bg-[#FAFBFC]">
                  <div className="px-4 py-2 flex items-center gap-2.5 flex-nowrap min-w-0 whitespace-nowrap">
                  {/* Sort */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10px] font-medium text-[#717182] uppercase tracking-wide whitespace-nowrap">Sort:</span>
                    <select
                      value={sortOption}
                      onChange={(e) => setSortOption(e.target.value as SortOption)}
                      className="text-[11px] font-medium text-[#18365E] border border-[#e5e7eb] rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 cursor-pointer"
                    >
                      <option value="rank">Rank</option>
                      <option value="roi_high">Highest value</option>
                      <option value="time_to_value">Fastest</option>
                      <option value="effort">Easiest First</option>
                      <option value="confidence">Confidence</option>
                    </select>
                  </div>

                  <div className="w-px h-4 bg-[#e5e7eb] shrink-0" />

                  {/* Category */}
                  <div className="flex items-center gap-1.5 min-w-0 shrink">
                    <label className="text-[10px] font-medium text-[#717182] uppercase tracking-wide whitespace-nowrap">Category:</label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="w-[260px] min-w-[110px] shrink truncate text-[11px] text-[#18365E] border border-[#e5e7eb] rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 cursor-pointer"
                    >
                      {uniqueCategories.map(cat => (
                        <option key={cat} value={cat}>{cat === 'all' ? 'All' : cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="w-px h-4 bg-[#e5e7eb] shrink-0" />

                  {/* {Client}: Projects / Committed, each Include / Exclude / Only */}
                  <div className="flex items-center gap-2 shrink-0" role="group" aria-label={`${MOCK_CLIENT_NAME} filters`}>
                    <span className="text-[10px] font-medium text-[#717182] uppercase tracking-wide">{MOCK_CLIENT_SHORT}:</span>
                    {([
                      ['Projects', projMode, setProjMode, acmeCounts.projects],
                      ['Committed', comMode, setComMode, acmeCounts.committed],
                    ] as const).map(([label, mode, setMode, count]) => (
                      <label key={label} className="flex items-center gap-1 text-[11px] font-medium text-[#18365E]">
                        {label} <span className="text-[#717182] tabular-nums">{count}</span>
                        <select value={mode} onChange={(e) => setMode(e.target.value as AcmeMode)} aria-label={`${label} filter`}
                          className={`text-[11px] border rounded-md pl-1.5 pr-5 py-1 focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 cursor-pointer ${mode === 'include' ? 'border-[#e5e7eb] bg-white text-[#18365E]' : 'border-[#18365E] bg-[#18365E] text-white'}`}>
                          <option value="include">Include</option>
                          <option value="exclude">Exclude</option>
                          <option value="only">Only</option>
                        </select>
                      </label>
                    ))}
                  </div>

                  <div className="w-px h-4 bg-[#e5e7eb] shrink-0" />

                  {/* Portfolio: Include / Exclude / Only the user's own picks */}
                  <label className="flex items-center gap-1 text-[11px] font-medium text-[#18365E] shrink-0" data-testid="portfolio-filter">
                    Portfolio <span className="text-[#717182] tabular-nums">{acmeCounts.portfolio}</span>
                    <select value={portMode} onChange={(e) => setPortMode(e.target.value as AcmeMode)} aria-label="Portfolio filter"
                      className={`text-[11px] border rounded-md pl-1.5 pr-5 py-1 focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 cursor-pointer ${portMode === 'include' ? 'border-[#e5e7eb] bg-white text-[#18365E]' : 'border-[#18365E] bg-[#18365E] text-white'}`}>
                      <option value="include">Include</option>
                      <option value="exclude">Exclude</option>
                      <option value="only">Only</option>
                    </select>
                  </label>
                  </div>
                </div>
  ) : null;

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  /**
   * Switching tabs rewrites ?tab= so the URL always describes what is on screen.
   * `replace` — a tab switch is not a new destination, and stacking one history
   * entry per click would make Back walk the tabs instead of leaving the page.
   * ?focus= is dropped: it belongs to the arrival, not to wherever you go next.
   */
  const handleTabChange = (next: EvaluateTab) => {
    setActiveTab(next);
    if (!comTouched) setComModeRaw(comDefaultFor(next));
    const params = new URLSearchParams(location.search);
    params.set('tab', next);
    params.delete('focus');
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, {
      replace: true,
      state: location.state,
    });
  };

  /**
   * FOCUS ON ARRIVAL. A returning export names its use case in ?focus=; that row
   * may sit on any page of the ranked list, so paginate to it first, then scroll
   * it into view. The ring itself is driven by the same id at render time.
   */
  useEffect(() => {
    if (!focusUseCaseId || activeTab !== 'explore') return;
    const index = sortedOpportunities.findIndex((o) => o.id === focusUseCaseId);
    if (index < 0) return;

    const page = Math.floor(index / ITEMS_PER_PAGE) + 1;
    if (page !== currentPage) {
      setCurrentPage(page);
      return; // re-runs once the row is actually mounted
    }
    const frame = requestAnimationFrame(() => {
      document
        .querySelector(`[data-explorer-row="${focusUseCaseId}"]`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [focusUseCaseId, activeTab, sortedOpportunities, currentPage]);

  const handleNavigate = (path: string, useCaseId?: string) => {
    const restoreState: RestoreState = {
      scrollY: window.scrollY,
      useCaseId: useCaseId || '',
    };
    navigate(path, {
      state: {
        restore: restoreState,
        from: location.pathname,
        // Carries ?compare= back with us, so a comparison survives a detour into a brief.
        returnTo: location.pathname + location.search,
      },
    });
  };

  const handleNavigateToWeights = () => {
    navigate('/weights', {
      state: { returnTo: location.pathname, restoreScrollY: window.scrollY }
    });
  };

  const handleBack = () => navigate('/home');
  const handleContinue = () => navigate('/activate');

  // Compare works off the ranked list, so a candidate carries the same rank it shows
  // in the explorer. Ids that no longer resolve (stale ?compare= param) are dropped.
  //
  // The param is hand-editable, so re-apply the entitlement gate here: without this a
  // freemium user could type a locked id into ?compare= and read its verdict, scores and
  // ROI out of the table — the same content EALockedOpportunityRow withholds in the list.
  const compareBriefs = useMemo(
    () =>
      compare.compareIds
        .map((id) => rankedOpportunities.find((o) => o.id === id))
        .filter((o): o is (typeof rankedOpportunities)[number] => Boolean(o))
        .filter((o) => canViewOpportunityDetail(o.rank))
        .map((o) => getDecisionBrief(o, o.rank)),
    [compare.compareIds, rankedOpportunities, canViewOpportunityDetail],
  );

  // Gate on what is actually renderable, not on the raw param: after unresolvable ids and
  // entitlement-gated ones are dropped there may be too few candidates left to compare.
  const showCompare = compare.compareMode && compareBriefs.length >= MIN_COMPARE;

  // Execute is Pro; for freemium the verdict-verb button is the upgrade moment.
  const handleProceed = (id: string) => {
    if (!isPro) {
      navigate('/upgrade');
      return;
    }
    proceed(id);
  };

  // Per-row Add/Remove-to-Portfolio (restores the original one-click add from the ranked list).
  // TOGGLE; adding a finalist flows to BOTH consumers (Portfolio Strategy report + Execute
  // list) since both read finalistIds. Only reachable from rendered ExplorerRows — the freemium
  // top-3 and all Pro rows; ranks 4+ in freemium are EALockedOpportunityRow (no add), so no gate
  // is needed here. Portfolio starts empty in both tiers; nothing is auto-added.
  const handleTogglePortfolio = (id: string) => {
    toggleFinalist(id);
  };


  return (
    <div className="min-h-screen bg-[#F5F7FA]">

      {evalRun.isRunning && (
        <EAEvaluationRunOverlay visibleStages={evalRun.visibleStages} onSkip={evalRun.skip} />
      )}

      {/* ── Sticky Header: Workspace label + Journey Bar + Tab Bar ── */}
      <EAJourneyBarFrame sticky bordered isPro={isPro}>
        <EATabBar
          activeTab={activeTab}
          onChange={handleTabChange}
          showAdjustWeights={activeTab === 'explore' && areCategoryWeightsCustomized}
          onAdjustWeights={handleNavigateToWeights}
        />
      </EAJourneyBarFrame>

      {/* ── Main Content ── */}
      {/* Flush against the tab row above (EAJourneyBarFrame ends with zero bottom gap
          when it carries a tab slot) — no top padding here, by design. */}
      <main className="px-6 pb-24">

        {/* ════════════════════════════════════════
            TAB 1: PORTFOLIO STRATEGY REPORT
            ════════════════════════════════════════ */}
        {activeTab === 'report' && (
          <OIRReportView
            clientName={MOCK_CLIENT_NAME}
            rankedOpportunities={reportRanked}
            selectedOpportunities={reportSelected}
            top10={reportTop10}
            portfolioRangeLabel={portfolioRangeLabel}
            shortlistedRangeLabel={shortlistedRangeLabel}
            finalistCount={finalistCount}
            includedCount={includedOpportunityCount}
            onOpenExplorationTab={() => setActiveTab('explore')}
            onContinueToExecute={handleContinue}
            onNavigate={handleNavigate}
            onBack={handleBack}
          />
        )}

        {/* ════════════════════════════════════════
            TAB 3: PRIORITY MATRIX (Oct 21 v3)
            Financial Value x Ease of Implementation, then the prioritized list by VRI.
            Same filters as the Explorer.
            ════════════════════════════════════════ */}
        {activeTab === 'matrix' && (
          <>
            {filterBarEl && (
              <div className="mt-3 bg-white rounded-xl border border-[#e5e7eb] shadow-sm overflow-hidden mb-3">{filterBarEl}</div>
            )}
            <PriorityMatrix
              points={matrixPoints}
              portfolioIds={finalistIds}
              onOpen={(id) => handleNavigate(`/opportunity/${id}/brief`, id)}
              onTogglePortfolio={handleTogglePortfolio}
              headerSlot={(
                <>
                  <AsOfChip className="shrink-0" />
                  {activeFilterCount > 0 && (
                    <button onClick={handleClearFilters} className="text-[11px] font-semibold text-[#FF6E4C] hover:text-[#FF6E4C]/80 whitespace-nowrap">Clear filters ×</button>
                  )}
                </>
              )}
            />
          </>
        )}

        {/* ════════════════════════════════════════
            TAB 2: USE CASE EXPLORER
            ════════════════════════════════════════ */}
        {/* ── Side-by-side comparison (entered from the compare tray) ── */}
        {activeTab === 'explore' && showCompare && (
          <>
            <div className="mb-4 flex items-center gap-3">
              <button
                onClick={compare.exitCompare}
                className="flex items-center gap-1.5 text-xs font-semibold text-[#717182] hover:text-[#18365E] transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                All opportunities
              </button>
            </div>
            <h2 className="text-lg font-bold text-[#18365E] mb-1">Side-by-side comparison</h2>
            <p className="text-xs text-[#717182] mb-4">
              Comparing {compareBriefs.length} candidate{compareBriefs.length !== 1 ? 's' : ''}. Remove one here, or
              uncheck it in the list.
            </p>
            <CompareTable
              briefs={compareBriefs}
              portfolioIds={portfolio}
              onFocus={(id) => handleNavigate(`/opportunity/${id}/brief`, id)}
              onRemove={compare.remove}
              onProceed={handleProceed}
            />
          </>
        )}

        {activeTab === 'explore' && !showCompare && (
          <>
            {/* ── Row 1: Opportunity Portfolio framing line. Vertically centered between the tab
                 frame above (main has no top padding by design) and the filter card below. ── */}
            <div className="py-3 flex items-center gap-3">
              <span className="text-sm font-semibold text-[#18365E] shrink-0">Opportunity Portfolio</span>
              <AsOfChip className="shrink-0 hidden md:inline-flex" />
              {activeFilterCount > 0 && (
                <button
                  onClick={handleClearFilters}
                  className="text-[11px] font-semibold text-[#FF6E4C] hover:text-[#FF6E4C]/80 transition-colors whitespace-nowrap"
                >
                  Clear filters ×
                </button>
              )}

              {/* Portfolio status — pushed to right */}
              <div className="ml-auto flex items-center gap-2 shrink-0">
                {finalistCount > 0 ? (
                  <>
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <span className="text-[#717182]">Portfolio:</span>
                      <span className="font-semibold text-[#18365E]">
                        {finalistCount} initiative{finalistCount !== 1 ? 's' : ''}
                      </span>
                      <span className="text-[#d1d5db]">|</span>
                      <span className="font-semibold text-[#18365E]">
                        ${selectedRoiLow.toFixed(1)}M–${selectedRoiHigh.toFixed(1)}M
                      </span>
                    </div>
                    <button
                      onClick={handleContinue}
                      className="flex items-center gap-1 px-2.5 py-1 bg-[#FF6E4C] text-white rounded-lg text-[11px] font-semibold hover:bg-[#FF6E4C]/90 transition-colors whitespace-nowrap"
                    >
                      Continue to Execute
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </>
                ) : (
                  <span className="text-[11px] text-[#717182]/50 italic whitespace-nowrap">
                    Portfolio: 0 selected · No portfolio value yet
                  </span>
                )}
              </div>
            </div>

            {/* Workbench Card */}
            <div className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm overflow-hidden mb-6">

              {/* ── Row 2: Filters (Pro only), shared with the Priority Matrix tab ── */}
              {filterBarEl}



              {/* Column headers */}
              {paginatedOpportunities.length > 0 && (
                <div className={`${EXPLORER_COLS.track} py-2.5 bg-[#F5F7FA] border-b border-[#e5e7eb] text-[10px] font-semibold text-[#717182] uppercase tracking-wide`}>
                  <span className={EXPLORER_COLS.rank}>Rank</span>
                  <span className={EXPLORER_COLS.title}>Opportunity · Executive verdict</span>
                  <span className={EXPLORER_COLS.value}>Est. value</span>
                  <span className={EXPLORER_COLS.ttv}>Time to value</span>
                  <span className={EXPLORER_COLS.effort}>Effort</span>
                  <span className={EXPLORER_COLS.actions}>Compare · Open</span>
                </div>
              )}

              {/* Initiative rows */}
              <div>
                {paginatedOpportunities.length === 0 ? (
                  <div className="px-5 py-12 text-center">
                    <Filter className="w-10 h-10 text-[#e5e7eb] mx-auto mb-3" />
                    <p className="text-sm font-medium text-[#18365E]">No opportunities match your filters</p>
                    <p className="text-xs text-[#717182] mt-1">Try adjusting your filter criteria</p>
                    <button onClick={handleClearFilters} className="mt-3 text-xs font-medium text-[#FF6E4C] hover:text-[#FF6E4C]/80">
                      Clear all filters
                    </button>
                  </div>
                ) : (
                  paginatedOpportunities.map((opportunity) => {
                    // Freemium: the full ranked list stays visible, but rows
                    // beyond the top-N are locked behind an upgrade affordance.
                    const isLocked = isFreemium && opportunity.rank > freemiumExplorerLimit;
                    if (isLocked) {
                      return (
                        <EALockedOpportunityRow
                          key={opportunity.id}
                          rank={opportunity.rank}
                          title={opportunity.title}
                          category={opportunity.category}
                          onUpgrade={() => navigate('/upgrade')}
                        />
                      );
                    }

                    const isCompared = compare.compareIds.includes(opportunity.id);

                    return (
                      <ExplorerRow
                        key={opportunity.id}
                        anchorId={opportunity.id}
                        focused={opportunity.id === focusUseCaseId}
                        brief={getDecisionBrief(opportunity, opportunity.rank)}
                        highlight={finalistIds.has(opportunity.id)}
                        commitment={commitments.get(opportunity.id)}
                        // Committed rows stay out of compare selection by default.
                        compareChecked={isCommitted(opportunity.id) && !isCompared ? undefined : isCompared}
                        compareDisabled={!isCompared && compare.isFull}
                        onToggleCompare={isCommitted(opportunity.id) && !isCompared ? undefined : () => compare.toggle(opportunity.id)}
                        onOpen={() => handleNavigate(`/opportunity/${opportunity.id}/brief`, opportunity.id)}
                        surfacedReasons={opportunity.surfacedReasons}
                        isInPortfolio={finalistIds.has(opportunity.id)}
                        onTogglePortfolio={() => handleTogglePortfolio(opportunity.id)}
                      />
                    );
                  })
                )}
              </div>

              {/* Pagination */}
              {isPro && totalPages > 1 && (
                <div className="px-5 py-2.5 border-t border-[#e5e7eb] bg-[#F5F7FA]/50">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#717182]">
                      Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, sortedOpportunities.length)} of {activeFilterCount > 0 ? sortedOpportunities.length : MOCK_TOTAL_COUNT} opportunities
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => goToPage(1)} disabled={currentPage === 1}
                        className={`p-1.5 rounded transition-colors ${currentPage === 1 ? 'text-[#e5e7eb] cursor-not-allowed' : 'text-[#717182] hover:text-[#18365E] hover:bg-white'}`}>
                        <ChevronsLeft className="w-4 h-4" />
                      </button>
                      <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
                        className={`p-1.5 rounded transition-colors ${currentPage === 1 ? 'text-[#e5e7eb] cursor-not-allowed' : 'text-[#717182] hover:text-[#18365E] hover:bg-white'}`}>
                        <ChevronLeft className="w-4 h-4" />
                      </button>

                      <div className="flex items-center gap-0.5 mx-1">
                        {(() => {
                          const pages: (number | '...')[] = [];
                          if (totalPages <= 7) {
                            for (let i = 1; i <= totalPages; i++) pages.push(i);
                          } else if (currentPage <= 4) {
                            pages.push(1, 2, 3, 4, 5, '...', totalPages);
                          } else if (currentPage >= totalPages - 3) {
                            pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                          } else {
                            pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
                          }
                          return pages.map((p, i) =>
                            p === '...' ? (
                              <span key={`ellipsis-${i}`} className="w-7 text-center text-xs text-[#717182]">…</span>
                            ) : (
                              <button key={p} onClick={() => goToPage(p as number)}
                                className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                                  currentPage === p ? 'bg-[#18365E] text-white' : 'text-[#717182] hover:bg-white hover:text-[#18365E]'
                                }`}>
                                {p}
                              </button>
                            )
                          );
                        })()}
                      </div>

                      <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
                        className={`p-1.5 rounded transition-colors ${currentPage === totalPages ? 'text-[#e5e7eb] cursor-not-allowed' : 'text-[#717182] hover:text-[#18365E] hover:bg-white'}`}>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages}
                        className={`p-1.5 rounded transition-colors ${currentPage === totalPages ? 'text-[#e5e7eb] cursor-not-allowed' : 'text-[#717182] hover:text-[#18365E] hover:bg-white'}`}>
                        <ChevronsRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Upgrade Teaser (Freemium Only) */}
            {isFreemium && (
              <div className="mb-6 p-5 bg-white border border-[#e5e7eb] rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#18365E]/10 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-[#18365E]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-[#18365E]">Unlock the full portfolio</h3>
                    <p className="text-xs text-[#717182] mt-1 leading-relaxed">
                      You're viewing the full ranked list. The top {freemiumExplorerLimit} use cases are open to explore;
                      upgrade to Pro to unlock every opportunity, apply filters, and build your portfolio.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/upgrade')}
                    className="px-4 py-2 bg-[#FF6E4C] text-white rounded-lg text-xs font-semibold hover:bg-[#FF6E4C]/90 transition-colors shrink-0"
                  >
                    Upgrade to Pro
                  </button>
                </div>
              </div>
            )}

            {/* Footer nav */}
            <div className="pt-4 border-t border-[#e5e7eb] flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 px-4 py-2 text-[#717182] hover:text-[#18365E] hover:bg-white rounded-lg transition-all text-sm font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Personalize
                </button>
                {showDemoControls() && (
                  <button
                    onClick={() => {
                      localStorage.setItem('dva_demo_plan_override', 'freemium');
                      window.location.reload();
                    }}
                    className="text-[10px] text-[#717182]/50 hover:text-[#717182] transition-colors"
                  >
                    Reset Demo
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                {isPro && finalistCount === 0 && (
                  <span className="text-xs text-[#717182]">Add at least one initiative to continue</span>
                )}
                <button
                  onClick={handleContinue}
                  disabled={!isPro || finalistCount === 0}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                    isPro && finalistCount > 0
                      ? 'bg-[#FF6E4C] text-white hover:bg-[#FF6E4C]/90 shadow-md'
                      : 'bg-[#e5e7eb] text-[#717182] cursor-not-allowed'
                  }`}
                >
                  Continue to Execute
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}

      </main>

      {/* Compare tray — visible whenever a comparison is being assembled */}
      {activeTab === 'explore' && !showCompare && (
        <CompareTray
          count={compare.compareIds.length}
          onCompare={compare.enterCompare}
          onClear={compare.clear}
        />
      )}

      {/* Chat FAB */}
      {!helpOpen && (
        <button
          onClick={() => setChatOpen(!chatOpen)}
          className={`fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg transition-all flex items-center justify-center z-50 ${
            chatOpen ? 'bg-[#717182]' : 'bg-[#18365E] hover:bg-[#18365E]/90'
          } text-white`}
        >
          {chatOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        </button>
      )}

      {/* Chat Panel */}
      {chatOpen && !helpOpen && (
        <div className="fixed bottom-24 right-6 w-80 h-96 bg-white rounded-xl shadow-2xl border border-[#e5e7eb] z-50 flex flex-col overflow-hidden">
          <div className="p-3 border-b bg-[#18365E]">
            <p className="text-[10px] text-white/70 font-medium uppercase tracking-wide">DVA Assistant</p>
            <p className="text-sm font-semibold text-white">Evaluation Help</p>
          </div>
          <div className="flex-1 p-4 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-10 h-10 text-[#e5e7eb] mx-auto mb-3" />
              <p className="text-sm font-medium text-[#18365E]">Coming Soon</p>
              <p className="text-xs text-[#717182] mt-1 max-w-[200px]">
                AI-powered help to guide your evaluation and selection process.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}